import { pdf } from '@react-pdf/renderer';
import { saveAs } from 'file-saver';
import JSZip from 'jszip';
import { useState } from 'react';

import {
  ITagOption,
  mapTagsToLabels,
} from '@/features/fermentation/utils/tags-label-mapper';
import { IBatch } from '@/features/single-measurement/types';
import { Tags } from '@/features/single-measurement/types/tags';
import { generateExportFilename } from '@/utils/generate-filename';
import { objectToCsv } from '@/utils/objects-to-csv';

import { formatBatch } from './format-batch';
import { getBatchImages } from './get-batch-images';
import { getHistogramGraph } from './get-histogram-graph';
import { formatBatchData } from './pdf/format-batches';
import { getFermentationPdfDocument } from './pdf/get-fermentation-pdf-document';
import { getPdfDocument } from './pdf/get-pdf-document';

export const useExport = () => {
  const [isExporting, setIsExporting] = useState<boolean>(false);
  const [isReportGenerating, setIsReportGenerating] = useState<boolean>(false);

  // generate csv for one or many
  const generateCSV = (batches: IBatch[]) => {
    const csvContent = objectToCsv([...batches.map((batch) => formatBatch(batch))]);
    return csvContent;
  };

  // generate zip bundle
  const generateExportZipBundle = async (batches: IBatch[]) => {
    if (!batches) return;
    setIsExporting(true);

    // add csv report into zip
    const zip = new JSZip();
    const csv = generateCSV(batches);
    const filename = generateExportFilename('_oculyze_export');
    zip.file(`${filename}/report.csv`, csv);

    // get images of each individual batch
    const images = await Promise.all(batches.map((batch) => getBatchImages(batch)));

    // add image bundle into zip
    for (const { taskId, batchId, imageType, image } of images.flat()) {
      const ext = image.type.includes('/') ? image.type.split('/')[1] : '.jpeg';
      const _filename = `${filename}/${batchId}/${taskId}_${imageType}.${ext}`;
      zip.file(_filename, image);
    }

    // add histogram graph
    for (const batch of batches) {
      const image = await getHistogramGraph(batch);
      if (image) {
        const _filename = `${filename}/${batch.id}/histogram.png`;
        zip.file(_filename, image);
      }
    }

    // save zip into local machine
    zip.generateAsync({ type: 'blob' }).then(function (content) {
      saveAs(content, filename + '.zip');
      setIsExporting(false);
    });
  };

  const generatePDFReport = async (batches: IBatch[]) => {
    if (!batches) return;
    setIsReportGenerating(true);

    const data = await formatBatchData(batches);

    // generate pdf document
    const pdfDocument = await getPdfDocument(data);
    const blob = await pdf(pdfDocument).toBlob();

    const filename = generateExportFilename('_oculyze_export.pdf');
    saveAs(blob, filename);

    setIsReportGenerating(false);
  };

  const generateFermentationPDFReport = async ({
    tags,
    image,
    batches,
  }: {
    image: string;
    tags?: Tags;
    batches: IBatch[];
  }) => {
    if (!batches) return;
    setIsReportGenerating(true);

    const tagList = tags ? mapTagsToLabels(tags) : ([] as ITagOption[]);

    const data = await formatBatchData(batches);

    // generate pdf document
    const pdfDocument = await getFermentationPdfDocument({
      data,
      tagList,
      fermentationGraph: image,
    });
    const blob = await pdf(pdfDocument).toBlob();

    const filename = generateExportFilename('_oculyze_export.pdf');
    saveAs(blob, filename);

    setIsReportGenerating(false);
  };

  return {
    isExporting,
    isReportGenerating,
    generatePDFReport,
    generateFermentationPDFReport,
    generateExportZipBundle,
  };
};
